import { useContext, useMemo, useState } from 'react';
import mainApi from '../../../../../assets/api/MainApi';
import {
    AVAILABLE_STATUS_MAP,
    getAvailableStatuses,
    parseApiError,
    parsePrice,
    parseRequestNumber,
} from '../../../../../assets/utils/utils';
import {
    NO_DELIVERY_TYPE,
    OWN_DELIVERY_TYPE,
} from '../../../../../assets/utils/constants';
import { UserContext } from '../../../../../assets/contexts/userContext';
import SubmitActionPopup from '../../../SubmitActionPopup/SubmitActionPopup';
import OrderPdfDownloadButton from '../OrderPdfDownloadButton/OrderPdfDownloadButton';
import './OrderControls.css';

function getReadyActionTitle(deliveryType) {
    if (deliveryType === OWN_DELIVERY_TYPE) {
        return 'Передать в доставку';
    }

    if (deliveryType === NO_DELIVERY_TYPE) {
        return 'Готов к выдаче';
    }

    return 'Отметить готовность';
}

function OrderControls({
    order,
    config,
    items,
    setOrder,
}) {
    const { user } = useContext(UserContext);
    const [pendingAction, setPendingAction] = useState(null);
    const [isSubmitOpen, setSubmitOpen] = useState(false);
    const [isSubmitPreloader, setSubmitPreloader] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const availableStatusMap = useMemo(() => {
        if (!order) return null;
        if (config) {
            return getAvailableStatuses({ config, order });
        }

        return AVAILABLE_STATUS_MAP;
    }, [config, order]);

    const availableStatuses = useMemo(() => {
        if (!order || !availableStatusMap) return [];
        return availableStatusMap[order.status] || [];
    }, [availableStatusMap, order]);

    const readyActionTitle = useMemo(
        () => getReadyActionTitle(order?.delivery_type),
        [order?.delivery_type],
    );

    const isOnlinePayment = order?.payment_type === 'yookassa';

    function closePopup() {
        setSubmitOpen(false);
        setSubmitError(null);
        setTimeout(() => {
            setPendingAction(null);
        }, 200);
    }

    function handleActionSelect(action) {
        setPendingAction(action);
        setSubmitOpen(true);
    }

    function handleSubmit() {
        if (!pendingAction || isSubmitPreloader || !order) return;

        const shop_id = user?.default_shop?._id;

        if (!shop_id) {
            setSubmitError('Не удалось определить магазин');
            return;
        }

        setSubmitPreloader(true);
        setSubmitError(null);

        mainApi.editStatus({
            shop_id,
            _id: order._id,
            payment_type: order.payment_type,
            status: pendingAction.targetStatus,
        })
            .then(() => {
                if (setOrder) {
                    setOrder((prev) => ({
                        ...prev,
                        status: pendingAction.targetStatus,
                    }));
                }
                closePopup();
            })
            .catch((err) => {
                setSubmitError(parseApiError(err)); // TODO: SHOW https://yookassa.ru/my/payments?search=30cc5340-000f-5001-8000-1b6ccad14c61 in error order.payment_id
            })
            .finally(() => {
                setSubmitPreloader(false);
            });
    }

    const controls = useMemo(() => {
        if (!order) return { actions: [] };

        const actions = [];

        const cancelAction = (override = {}) => ({
            key: 'cancel',
            title: isOnlinePayment ? 'Сделать возврат' : 'Отменить заказ',
            targetStatus: 'cancelled',
            tone: 'danger',
            ...override,
        });

        const addAction = (action) => {
            if (!action) return;
            if (availableStatuses.includes(action.targetStatus)) {
                actions.push(action);
            }
        };

        switch (order.status) {
            case 'new':
                addAction({
                    key: 'accept',
                    title: 'Принять заказ',
                    targetStatus: 'in_work',
                    tone: 'primary',
                });
                addAction(cancelAction());
                break;
            case 'paid':
                if (isOnlinePayment) {
                    addAction({
                        key: 'confirm-cart',
                        title: 'Подтвердить корзину',
                        targetStatus: 'in_work',
                        tone: 'primary',
                    });
                    addAction(cancelAction({
                        title: 'Сделать возврат',
                    }));
                } else {
                    addAction({
                        key: 'send-to-ready',
                        title: readyActionTitle,
                        targetStatus: 'ready',
                        tone: 'primary',
                    });
                    addAction(cancelAction());
                }
                break;
            case 'in_work':

                addAction({
                    key: 'ready',
                    title: readyActionTitle,
                    targetStatus: 'ready',
                    tone: 'primary',
                });
                if (!isOnlinePayment && availableStatuses.includes('paid')) {
                    addAction({
                        key: 'mark-paid',
                        title: 'Оплата получена',
                        targetStatus: 'paid',
                        tone: 'ghost',
                    });
                }

                addAction(cancelAction({
                    title: isOnlinePayment ? 'Сделать возврат' : 'Отменить заказ',
                }));
                break;
            case 'ready':
                addAction({
                    key: 'done',
                    title: 'Заказ выдан',
                    targetStatus: 'done',
                    tone: 'primary',
                });
                addAction(cancelAction({
                    title: isOnlinePayment ? 'Сделать возврат' : 'Отменить заказ',
                }));
                break;
            default:
                break;
        }

        return { actions };
    }, [availableStatuses, isOnlinePayment, order, readyActionTitle]);

    const confirmText = useMemo(() => {
        if (!pendingAction || !order) return '';

        const orderNumber = parseRequestNumber(order.ai_id);

        if (pendingAction.targetStatus === 'cancelled') {
            if (isOnlinePayment) {
                if (order.status === 'paid') {
                    return `Отменить заказ ${orderNumber} и произвести возврат замороженных средств?`;
                }
                return `Отменить заказ ${orderNumber} и произвести полный возврат средств клиенту (возможна комиссия платёжной системы)?`;
            }
            return `Отменить заказ ${orderNumber}?`;
        }

        switch (pendingAction.key) {
            case 'accept':
                return `Принять заказ ${orderNumber} в сборку?`;
            case 'confirm-cart':
                return `Подтвердить состав заказа ${orderNumber}, принять оплату и отправить в сборку?`;
            case 'ready':
            case 'send-to-ready':
                if (readyActionTitle === 'Передать в доставку') {
                    return `Передать заказ ${orderNumber} в доставку?`;
                }
                if (readyActionTitle === 'Готов к выдаче') {
                    return `Отметить, что заказ ${orderNumber} готов к выдаче?`;
                }
                return `Отметить готовность заказа ${orderNumber}?`;
            case 'mark-paid':
                return `Подтвердить, что оплата за заказ ${orderNumber} получена?`;
            case 'done':
                return `Подтвердить выдачу заказа ${orderNumber} клиенту и закрыть заказ?`;
            default:
                return `Подтвердить действие для заказа ${orderNumber}?`;
        }
    }, [isOnlinePayment, order, pendingAction, readyActionTitle]);

    const confirmCartPreview = useMemo(() => {
        if (!pendingAction || pendingAction.key !== 'confirm-cart' || !items || items.length === 0) return null;
        const discount = Number(order?.discount || 0);
        const total = items.reduce((acc, item) => {
            const price = discount
                ? Number(item.price) - (Number(item.price) / 100) * discount
                : Number(item.price);
            return acc + price * Number(item.amount || 0);
        }, 0);
        const count = items.reduce((acc, item) => acc + Number(item.amount || 0), 0);

        return (
            <div className="order-controls__cart-preview">
                <div className="order-controls__cart-preview-list">
                    {items.map((item) => {
                        const price = discount
                            ? Number(item.price) - (Number(item.price) / 100) * discount
                            : Number(item.price);
                        return (
                            <div
                                key={`${item._id}-${JSON.stringify(item.options || [])}-preview`}
                                className="order-controls__cart-preview-item"
                            >
                                <div className="order-controls__cart-preview-info">
                                    <span className="order-controls__cart-preview-name">
                                        {item?.item?.name || 'Без названия'}
                                    </span>
                                    {item?.options && item.options.length ? (
                                        <span className="order-controls__cart-preview-options">
                                            {item.options.map((opt) => `${opt.name}: ${opt.value}`).join(' • ')}
                                        </span>
                                    ) : null}
                                </div>
                                <div className="order-controls__cart-preview-meta">
                                    <span className="order-controls__cart-preview-amount">{item.amount} x</span>
                                    <span className="order-controls__cart-preview-price">
                                        {parsePrice(price * Number(item.amount || 0), 'RUB')}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="order-controls__cart-preview-footer">
                    <span className="order-controls__cart-total-title">ИТОГО</span>
                    <span className="order-controls__cart-total-price">
                        {parsePrice(total, 'RUB')}
                    </span>
                </div>
            </div>
        );
    }, [items, order?.discount, pendingAction]);

    if (!order) return null;

    return (
        <>
            <div
                className="order-controls"
                key={`order-controls-${order._id}-${order.status}`}
            >
                <div className="order-controls__main">
                    {controls.actions.map((action) => (
                        <button
                            type="button"
                            key={action.key}
                            className={`order-controls__btn order-controls__btn_${action.tone}`}
                            onClick={() => handleActionSelect(action)}
                        >
                            {action.title}
                        </button>
                    ))}

                </div>

                <div className="order-controls__extra">
                    <OrderPdfDownloadButton
                        order={order}
                        items={items}
                        className="order-controls__btn order-controls__btn_download"
                    />
                </div>

            </div>

            <SubmitActionPopup
                isSubmitPreloader={isSubmitPreloader}
                hendleSubmit={handleSubmit}
                hendleClose={closePopup}
                isOpen={isSubmitOpen}
                text={confirmText}
                submitText={'Продолжить'}
                cencelText={'Назад'}
                submitError={submitError}
                extraContent={confirmCartPreview}
            />
        </>
    );
}

export default OrderControls;
